/* eslint-disable @typescript-eslint/indent */
import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable, throwError } from "rxjs";
import { catchError } from "rxjs/operators";
import { environment } from "src/environments/environment";
import { PaginatedResponse } from "../../../core/http/paginated-response.type";
import { QueryParams } from "../../../core/http/query-params.type";
import { DEFAULT_QUERY_PARAMS } from "../../../shared/constants/services";
import { Asset, TotalContractAssetsPrices } from "../assets/asset.type";
import { Contract, ContractAttachment, ContractCountByEndDate, ContractRenewal } from "./contract.type";

@Injectable({
  providedIn: "root",
})
export class ContractService {
  path = "/contracts";
  basePath = `${environment.serverUrl}/contracts`;
  basePathCsvQueue = `${environment.serverUrl}/contracts/queue-csv`;
  basePathContractAttachment = `${environment.serverUrl}/contract-attachments`;

  constructor(private http: HttpClient) {}

  createContract(data): Observable<Contract> {
    return this.http.post<Contract>(this.basePath, data).pipe(catchError(this.handleError<any>()));
  }

  renewContract(data: ContractRenewal): Observable<Contract> {
    const { contractId, ...body } = data;
    return this.http
      .post<Contract>(`${this.basePath}/${contractId}/renew`, body)
      .pipe(catchError(this.handleError<any>()));
  }

  getContracts(options?: QueryParams<Contract>): Observable<PaginatedResponse<Contract>> {
    let params: HttpParams = new HttpParams();
    options = { ...DEFAULT_QUERY_PARAMS, ...options };
    for (const key in options) {
      if (options[key]) {
        params = params.set(key, JSON.stringify(options[key]));
      }
    }

    return this.http
      .get<PaginatedResponse<Contract>>(this.basePath, { params })
      .pipe(catchError(this.handleError<any>()));
  }

  getContractsQueue(options?: QueryParams<Contract>): Observable<void> {
    let params: HttpParams = new HttpParams();
    options = { ...DEFAULT_QUERY_PARAMS, ...options };
    for (const key in options) {
      if (options[key]) {
        params = params.set(key, JSON.stringify(options[key]));
      }
    }

    return this.http.get<void>(this.basePathCsvQueue, { params }).pipe(catchError(this.handleError<any>()));
  }

  getAvailableAssetsToLink(contractId: string, options?: QueryParams<Asset>): Observable<PaginatedResponse<Asset>> {
    let params: HttpParams = new HttpParams();
    options = { ...DEFAULT_QUERY_PARAMS, ...options };
    for (const key in options) {
      if (options[key]) {
        params = params.set(key, JSON.stringify(options[key]));
      }
    }

    return this.http
      .get<PaginatedResponse<Asset>>(`${this.basePath}/${contractId}/available-assets-to-link`, { params })
      .pipe(catchError(this.handleError<any>()));
  }

  getContract(contractId: string, options?: QueryParams<Contract>): Observable<Contract> {
    let params: HttpParams = new HttpParams();
    for (const key in options) {
      if (options[key]) {
        params = params.set(key, JSON.stringify(options[key]));
      }
    }
    return this.http
      .get<Contract>(`${this.basePath}/${contractId}`, { params })
      .pipe(catchError(this.handleError<any>()));
  }

  getS3Url(contractId: string, extension: string): Observable<{ url: string; name: string }> {
    return this.http
      .get<Contract>(`${this.basePath}/${contractId}/url-contract-attachments`, { params: { extension } })
      .pipe(catchError(this.handleError<any>()));
  }

  updateContract(contractId: string, data: Partial<Contract>): Observable<Contract> {
    return this.http.put<Contract>(`${this.basePath}/${contractId}`, data).pipe(catchError(this.handleError<any>()));
  }

  terminateContract(contractId: string, data: Partial<Contract>): Observable<Contract> {
    return this.http
      .put<Contract>(`${this.basePath}/${contractId}/terminate`, data)
      .pipe(catchError(this.handleError<any>()));
  }

  unterminateContract(contractId: string): Observable<Contract> {
    return this.http
      .put<Contract>(`${this.basePath}/${contractId}/unterminate`, {})
      .pipe(catchError(this.handleError<any>()));
  }

  updateContractAttachment(
    contractId: string,
    contractAttachmentId: string,
    data: Partial<ContractAttachment>,
  ): Observable<ContractAttachment> {
    return this.http
      .put<Contract>(`${this.basePath}/${contractId}/contract-attachments/${contractAttachmentId}`, data)
      .pipe(catchError(this.handleError<any>()));
  }

  getContractAttachments(
    contractId: string,
    options?: QueryParams<ContractAttachment>,
  ): Observable<PaginatedResponse<ContractAttachment>> {
    let params: HttpParams = new HttpParams();
    options = { ...DEFAULT_QUERY_PARAMS, ...options };
    for (const key in options) {
      if (options[key]) {
        params = params.set(key, JSON.stringify(options[key]));
      }
    }
    return this.http
      .get<PaginatedResponse<ContractAttachment>>(`${this.basePath}/${contractId}/contract-attachments`, {
        params,
      })
      .pipe(catchError(this.handleError<any>()));
  }

  createAttachment(
    contract: Pick<ContractAttachment, "name" | "url" | "description">,
    contractId: string,
  ): Observable<ContractAttachment> {
    return this.http
      .post<Observable<ContractAttachment>>(`${this.basePath}/${contractId}/contract-attachments`, contract)
      .pipe(catchError(this.handleError<any>()));
  }

  getCountByEndDate(representativeId?: string): Observable<ContractCountByEndDate> {
    let params: HttpParams = new HttpParams();

    if (representativeId) {
      params = params.set("where", JSON.stringify({ representativeId }));
    }

    return this.http
      .get<ContractCountByEndDate>(`${this.basePath}/count-by-end-date`, { params })
      .pipe(catchError(this.handleError<any>()));
  }

  linkAssets(contractId: string, body): Observable<void> {
    return this.http.put<void>(`${this.basePath}/${contractId}/assets`, body).pipe(catchError(this.handleError<any>()));
  }

  getTotalPrices(contractId: string): Observable<TotalContractAssetsPrices> {
    return this.http
      .get<void>(`${this.basePath}/${contractId}/contract-assets/prices`)
      .pipe(catchError(this.handleError<any>()));
  }

  linkAttachment(contractId: string, body): Observable<void> {
    return this.http.put<void>(`${this.basePath}/${contractId}/assets`, body).pipe(catchError(this.handleError<any>()));
  }

  getWelcomeLetter(contractId: string): Observable<Contract> {
    const options = { responseType: "blob" as "json" };
    return this.http
      .get<void>(`${this.basePath}/${contractId}/welcome-letter`, options)
      .pipe(catchError(this.handleError<any>()));
  }

  private handleError<T>() {
    return (error: any): Observable<T> => {
      console.error(error);
      return throwError(error);
    };
  }
}
